import { createFileRoute, Link } from "@tanstack/react-router";
import { RelatedLinks } from "@/components/related-links";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { EQUIPMENT, EQUIPMENT_CATEGORIES, type Equipment } from "@/data/equipment";
import { getPublicEquipmentOverrides, type SiteEquipmentOverride } from "@/lib/site-content.functions";
import { useReveal } from "@/hooks/use-reveal";
import { PageHero } from "@/components/page-hero";
import heroImg from "@/assets/dj-mariage-equipment.jpg";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
import { LOCAL_BUSINESS_SCHEMA } from "@/lib/schema-org";
export const Route = createFileRoute("/location-materiel/")({
  head: () => ({
    meta: [
      { title: "Location sono Vaucluse : matériel DJ pro | DJ Val" },
      {
        name: "description",
        content:
          metaDesc("Location de sono, éclairage LED, photobooth, karaoké et vidéoprojecteur dans le Vaucluse, Avignon et Cavaillon. Livraison et installation possibles, devis gratuit en 24 h.")
      },
      { property: "og:title", content: "Location Sono & Matériel DJ — Vaucluse / Avignon | DJ Val" },
      { property: "og:description", content: metaDesc("Sono RCF, console Pioneer, lyres LED, lasers, photobooth, canon CO₂. +1000 locations réalisées en Provence.") },
      { property: "og:url", content: "https://dj-val.fr/location-materiel" },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/location-materiel" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Location Sono & Matériel DJ — Vaucluse / Avignon",
          serviceType: "Location de matériel sonorisation, éclairage et effets",
          provider: { ...LOCAL_BUSINESS_SCHEMA },
          areaServed: ["Vaucluse", "Bouches-du-Rhône", "Gard", "Avignon", "Cavaillon", "Aix-en-Provence", "Arles", "Nîmes"],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Où louer du matériel sono pro dans le Vaucluse ?", acceptedAnswer: { "@type": "Answer", text: "DJ Val loue du matériel sono professionnel (RCF, Electro-Voice EKX-15P, Pioneer CDJ-3000 · DJM-A9) à Cavaillon, Avignon, Aix-en-Provence et dans tout le Vaucluse, avec option livraison et installation sur site." } },
            { "@type": "Question", name: "Combien coûte la location d'une sono pour soirée à Avignon ?", acceptedAnswer: { "@type": "Answer", text: "À partir de 120 € la journée pour une sono 80 personnes. Pour un mariage 150 personnes : autour de 250 €/jour. Line Array grand événement sur devis." } },
            { "@type": "Question", name: "La livraison et l'installation sont-elles incluses ?", acceptedAnswer: { "@type": "Answer", text: "Option à la carte. Aucun frais dans un rayon de 80 km autour de Cavaillon, forfait transparent au-delà (0,60 €/km). Installation, calage et démontage par DJ Val." } },
            { "@type": "Question", name: "Peut-on louer un photobooth à Avignon ?", acceptedAnswer: { "@type": "Answer", text: "Oui — borne photobooth à écran tactile (impression 10×15 illimitée + bac d'accessoires) à louer à Avignon, Cavaillon, Aix et tout le Vaucluse. À partir de 190 € la soirée, tirages illimités, retrait à Cavaillon ; livraison et installation en option." } },
            { "@type": "Question", name: "Faut-il une caution ?", acceptedAnswer: { "@type": "Answer", text: "Oui, une caution est demandée à la signature puis restituée intégralement au retour du matériel en bon état." } },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  useReveal();
  const [filter, setFilter] = useState<string>("Tous");
  const fetchOverrides = useServerFn(getPublicEquipmentOverrides);
  const [overrides, setOverrides] = useState<SiteEquipmentOverride[]>([]);
  useEffect(() => {
    fetchOverrides().then((r) => setOverrides(r.rows)).catch(() => {});
  }, [fetchOverrides]);

  const items = useMemo(() => {
    const map = new Map(overrides.map((o) => [o.slug, o] as const));
    const merged: (Equipment & { _ordre: number })[] = [];
    EQUIPMENT.forEach((e, i) => {
      const o = map.get(e.slug);
      if (o && !o.actif) return; // masqué
      merged.push({
        ...e,
        name: o?.name ?? e.name,
        short: o?.short ?? e.short,
        text: o?.description ?? e.text,
        priceFrom: o?.price_from ?? e.priceFrom,
        image: o?.image ?? e.image,
        _ordre: o?.ordre ?? i * 100,
      });
    });
    merged.sort((a, b) => a._ordre - b._ordre);
    return merged;
  }, [overrides]);

  const filtered = filter === "Tous" ? items : items.filter((e) => e.category === filter);

  return (
    <div className="bg-background text-muted-foreground">
      <PageHero
        image={heroImg}
        eyebrow="Location professionnelle"
        title={<>Tout le matériel<span className="block italic">à la location</span></>}
        subtitle="Sonorisation, éclairage scénique, vidéo, photobooth, effets spéciaux. Matériel professionnel, livraison et installation en option en Provence."
        primary={{ label: "Devis location", to: "/contactez-nous" }}
        secondary={{ label: "Conditions", to: "/conditions-de-location" }}
        height="compact"
      />
      <ReviewsProof />

      <section className="py-12 px-6 border-b border-foreground/10 sticky top-16 lg:top-20 z-30 bg-background/95 backdrop-blur">

        <div className="max-w-6xl mx-auto flex flex-wrap justify-center gap-2">
          {["Tous", ...EQUIPMENT_CATEGORIES].map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-5 py-2 text-[10px] uppercase tracking-[0.25em] border transition-colors ${
                filter === c
                  ? "border-[var(--gold)] text-foreground bg-[var(--gold)]/10"
                  : "border-foreground/10 text-muted-foreground hover:text-foreground"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16 px-6 max-w-7xl mx-auto">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((e, i) => (
            <Link key={e.slug} to="/location-materiel/$slug" params={{ slug: e.slug }} className="group sr block border border-foreground/10 bg-card overflow-hidden hover:border-foreground transition-colors" data-reveal style={{ transitionDelay: `${(i % 3) * 80}ms` }}>
              <div className="aspect-[4/3] overflow-hidden">
                <img loading="lazy" decoding="async" src={e.image} alt={`Location ${e.name} – ${e.category.toLowerCase()} en Vaucluse, Avignon et Cavaillon`} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <p className="eyebrow text-foreground text-[10px]">{e.category}</p>
                <h2 className="font-serif-display text-xl text-foreground mt-2">{e.name}</h2>
                <p className="text-xs text-muted-foreground italic mt-1">{e.short}</p>
                <span className="gold-rule !w-8 my-3" />
                <p className="text-sm text-muted-foreground leading-relaxed font-light line-clamp-3">{e.text}</p>
                <div className="mt-5 flex items-center justify-between border-t border-foreground/10 pt-4">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-foreground">{e.priceFrom}</span>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-foreground group-hover:text-foreground transition-colors">Voir la fiche →</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="py-24 px-6 text-center border-t border-foreground/10 bg-card">
        <div className="max-w-2xl mx-auto sr" data-reveal>
          <p className="eyebrow text-foreground">Devis personnalisé</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">
            Besoin d'un devis <span className="italic">location</span> ?
          </h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <p className="text-sm text-muted-foreground mb-8 font-light">Livraison, installation et reprise possibles selon votre besoin. Réponse sous 24 h.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/contactez-nous" className="inline-block bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all">
              Demander un devis
            </Link>
            <Link to="/dj-mariage/pack-sono-lumiere" className="inline-block border border-foreground/20 text-foreground px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
              Voir le pack DJ mariage
            </Link>
          </div>
        </div>
      </section>
      <RelatedLinks group="materiel" />

    </div>
  );
}
